<template>
  <div class="table-wrapper">
    <!--
      keep-alive 缓存三种表格模式（标准 / 透视表 / 透视图）的组件实例。
      左侧切换 tab 时，被隐藏的组件不会被销毁，其已加载的数据与视图状态
      保留在内存中，再次切回时可以瞬间渲染，无需重新拉取数据。
      注意：三个组件各自绑定独立的 config，避免共用同一份 config 导致
      缓存实例在重新激活时被错误 prop 覆盖。
    -->
    <keep-alive>
      <template v-if="normalConfig && tableMode === 'normal'">
        <a-alert
          v-if="filter_id_key"
          type="warning"
          closable
          show-icon
          @close="onClearFilterIdkey"
        >
          <template #message>
            <div class="filter-message">
              <span
                v-for="item in filterList"
                :key="item.label"
              >
                {{ item.label }}:
                <a-tag>{{ item.value }}</a-tag>
              </span>
              <span>
                值字段:
                <a-tag>{{ matchValueLabel }}</a-tag>
              </span>
            </div>
            <div>
              当前数据已通过透视表定位，此功能为临时过滤，不会被视图记录。关闭可恢复原始数据。
            </div>
          </template>
        </a-alert>

        <tablePro
          :config="normalConfig"
          ref="tableProRef"
          :eventBus="eventBus"
          v-model:heightlightRowKey="heightlightRowKey"
          @heightlightRowChange="handleHeightlightRowChange"
        >
          <template #header_bar_before_title>
            <div v-if="pageConfig.isView">
              <div
                class="toggle-button"
                @click="toggleOpen"
              >
                <MenuFoldOutlined v-if="leftOpen" />
                <MenuUnfoldOutlined v-else />
              </div>
            </div>
          </template>
          <template #header_bar_before_btn>
            <btn-add
              v-if="enable_btn_add"
              :modalType="modalType"
              :app_flag="app_flag"
              :pageConfig="pageConfig"
              :dataSource="tableProRef?.dataSource"
              :selectedRowKeys="tableProRef?.selectedRowKeys"
              @ok="handleAdd"
            />
          </template>
        </tablePro>
      </template>
      <pivotTable
        v-else-if="pivotConfig && tableMode === 'pivot'"
        :config="pivotConfig"
        ref="pivotTableRef"
        :eventBus="eventBus"
        @filter-for-id-key-start="handleFilterForIdKeyStart"
        @filterForIdKey="handleFilterForIdKey"
      >
        <template #header_bar_before_title>
          <div v-if="pageConfig.isView">
            <div
              class="toggle-button"
              @click="toggleOpen"
            >
              <MenuFoldOutlined v-if="leftOpen" />
              <MenuUnfoldOutlined v-else />
            </div>
          </div>
        </template>
        <template #header_bar_before_btn>
          <btn-add
            v-if="enable_btn_add"
            :modalType="modalType"
            :app_flag="app_flag"
            :pageConfig="pageConfig"
            :dataSource="pivotTableRef?.dataSource"
            :selectedRowKeys="pivotTableRef?.selectedRowKeys"
            @ok="handleAdd"
          />
        </template>
      </pivotTable>
      <pivotCharts
        v-else-if="pivotChartsConfig && tableMode === 'pivot_charts'"
        :config="pivotChartsConfig"
        ref="pivotChartsRef"
        :eventBus="eventBus"
      >
        <template #header_bar_before_title>
          <div v-if="pageConfig.isView">
            <div
              class="toggle-button"
              @click="toggleOpen"
            >
              <MenuFoldOutlined v-if="leftOpen" />
              <MenuUnfoldOutlined v-else />
            </div>
          </div>
        </template>
        <template #header_bar_before_btn>
          <btn-add
            v-if="enable_btn_add"
            :modalType="modalType"
            :app_flag="app_flag"
            :pageConfig="pageConfig"
            :dataSource="pivotChartsRef?.dataSource"
            :selectedRowKeys="pivotChartsRef?.selectedRowKeys"
            @ok="handleAdd"
          />
        </template>
      </pivotCharts>
    </keep-alive>

    <!-- 透视表双击定位时的全局 loading 遮罩：接口可能耗时较久，覆盖整页防止误操作 -->
    <FilterLoading :visible="filterLoading" />
  </div>
</template>
<script setup lang="ts">
import tablePro from 'liyu-pc-base/components/tablePro/tablePro.vue';
import pivotTable from 'liyu-pc-base/components/tablePro/pivotTable.vue';
import pivotCharts from 'liyu-pc-base/components/tablePro/pivotCharts.vue';
import {
  computed,
  onMounted,
  onUnmounted,
  onActivated,
  onDeactivated,
  ref,
  watch,
  nextTick,
  h,
} from 'vue';
import type { PageConfig } from '../typing';
import type {
  SearchGroupItem,
  Table,
  SearchConfig,
  RefreshType,
  RowSelectionType,
} from 'liyu-pc-base/components/tablePro/typing';
import btnAdd from './btnAdd.vue';
import FilterLoading from './FilterLoading.vue';
import { useRoute } from 'vue-router';
import { genID, registerCustomPageAbility, registerQuickAction } from 'liyu-pc-base/utils/function';
import { useUserStore, useEnv } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { message, Modal } from 'ant-design-vue';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import {
  create_view_settings,
  update_view_settings,
  delete_view_settings,
  set_view_settings_default,
  refresh_view_settings_cache,
} from 'liyu-pc-base/api/view-model';
import { fetchViewsData } from 'liyu-pc-base/components/tablePro/utils';
import {
  isViewType,
  validateViewConfigParams,
  validateViewData,
  normalizeViewData,
  findViewByUidOrName,
  type ViewType,
} from './ai-view-helper';
import {
  ViewCapabilityParamsSchema,
  ViewConfigDataParamsSchema,
  ViewLookupParamsSchema,
  ViewConfigParamsSchema,
  ViewUpdateParamsSchema,
  ViewRenameParamsSchema,
  type ViewCapabilityParams,
  type ViewConfigDataParams,
  type ViewLookupParams,
  type ViewConfigParams,
  type ViewUpdateParams,
  type ViewRenameParams,
} from './ai-view-ability-schemas';
import { event_obj } from 'liyu-pc-base/init-plugin';
// 三种表格模式各自独立的 config：
// keep-alive 缓存隐藏的组件实例，若共用同一个 config，重新激活时 prop 会
// 被其它模式的 config 覆盖。拆分为三个后才能保证缓存实例数据不被污染。
const normalConfig = ref<Table>(null);
const pivotConfig = ref<Table>(null);
const pivotChartsConfig = ref<Table>(null);

interface Props {
  eventBus: any;
  pageConfig: PageConfig;
  modalType: string;
  app_flag: string;
  tableMode?: 'normal' | 'pivot' | 'pivot_charts';
  setting_drawer_width: number;
  // ===== 以下为「详情表（detailTablePro）复用」用的可选注入点，默认值与页面级行为完全一致 =====
  base_conditions_inject?: any[]; // 预过滤条件，追加到 advanced_base_conditions（强制走 conditions 通道）
  query_params_inject?: Record<string, any>; // 追加到 query_params 的额外参数
  enable_ai_abilities?: boolean; // 是否注册 AI 页面能力 / 快速操作
  enable_hash_sync?: boolean; // 是否读写 window.location.hash（视图同步）
  enable_btn_add?: boolean; // 是否显示表头「新建」按钮
  view_key_namespace?: string; // 视图 key 命名空间（隔离详情表的视图配置）
  inherit_route_params?: boolean; // 是否把当前路由 query 作为基础条件（详情表默认关）
  title_inject?: string; // 覆盖表格标题（详情表传目标页面标题，避免沿用主页面路由标题）
  enable_advanced_search_multigroups?: boolean; // 多组查询；undefined = 跟随目标页面 is_show_advantage（主页面现状），布尔 = 强制（详情表恒传，默认 false 单组）
  search_display_type?: 'hide' | 'hide_and_show_summary' | 'show' | 'show_no_hide'; // 覆盖 tablePro 搜索栏显示方式；默认 'show'（主页面）；详情表由 detailTablePro 传入 'hide_and_show_summary'
}
const props = withDefaults(defineProps<Props>(), {
  eventBus: null,
  pageConfig: null,
  tableMode: 'normal',
  base_conditions_inject: () => [],
  query_params_inject: () => ({}),
  enable_ai_abilities: true,
  enable_hash_sync: true,
  enable_btn_add: true,
  view_key_namespace: 'remotePagesV2',
  inherit_route_params: true,
  title_inject: '',
  enable_advanced_search_multigroups: undefined,
  search_display_type: 'show',
});
const emit = defineEmits(['changeMode']);
const userStore = useUserStore();
const { t } = useI18n();
const route = useRoute();
const handleAdd = () => {
  activeTableRef.value?.refresh();
};
const tableProRef = ref(null);
const pivotTableRef = ref(null);
const pivotChartsRef = ref(null);
const filter_id_key = ref('');
const filterList = ref<{ label: string; value: string }[]>([]);
const matchValueLabel = ref('');
// 透视表双击定位时的全局 loading 遮罩：filterForIdKeyStart 打开，filterForIdKey 关闭
const filterLoading = ref(false);
const activeTableRef = computed(() => {
  if (props.tableMode === 'pivot') return pivotTableRef.value;
  if (props.tableMode === 'pivot_charts') return pivotChartsRef.value;
  return tableProRef.value;
});
const leftOpen = defineModel('leftOpen', {
  default: true,
});
const heightlightRowKey = defineModel('heightlightRowKey', {
  default: null,
});

const WRITE_PROMPT = '写操作,执行前应先与用户确认意图';

// 三种表格模式在挂载时一次性构建各自的 config。
// 切换 tableMode 不再销毁/重建组件，而是由 keep-alive 缓存隐藏实例，
// 已加载的数据随实例保留，切回时即时显示，无需重新拉取。
onMounted(() => {
  normalConfig.value = getConfig('normal');
  pivotConfig.value = getConfig('pivot');
  pivotChartsConfig.value = getConfig('pivot_charts');
  // 普通挂载/刷新场景(组件不在 keep-alive 内时 onActivated 不触发,必须在此注册)
  // 详情表不注册 AI 页面能力：能力注册表按名字取栈顶存活项，详情表注册会顶掉主页面的能力
  if (props.enable_ai_abilities) {
    registerPageAbilities();
  }
});
// 详情表注入的预过滤条件 / 附加参数变化时（例如主表切换到另一行）重建三种模式的 config。
// tablePro 内部对 config.base_conditions 与 config.query_params 都是深度 watch 并触发重新查询，
// 因此重建 config 即可让表格按新条件重新取数；主页面从未传注入参数，watch 不会触发，行为不变。
const rebuildConfigs = () => {
  normalConfig.value = getConfig('normal');
  pivotConfig.value = getConfig('pivot');
  pivotChartsConfig.value = getConfig('pivot_charts');
};
watch(
  [() => props.base_conditions_inject, () => props.query_params_inject],
  () => {
    rebuildConfigs();
  },
  { deep: true },
);
const handleHeightlightRowChange = (record: any) => {
  const key = props.pageConfig.row_click_detail_event_key;
  const payload = {
    key,
    cmpObj: { record },
  };
  // 整行点击与单元格点击（cell_detail）走同一条页面实例总线：props.eventBus 由页面壳注入
  // （详情表场景是该层自己的本地 mitt，嵌套详情互不串扰）；未传时退回全局单例兜底。
  const bus = props.eventBus || event_obj();
  bus.emit('show_detail', payload);
};
// AI 助手页面能力 + 快速操作按钮注册。
// 组件自身被路由级 keep-alive 缓存时 onMounted/onUnmounted 只触发一次,
// 由 onActivated/onDeactivated 跟随页面显隐注册与注销;两种场景都覆盖。
const unregisterAbilityFns: (() => void)[] = [];
const clearRegisteredAbilities = () => {
  unregisterAbilityFns.forEach(unregister => unregister());
  unregisterAbilityFns.length = 0;
};
const registerPageAbilities = () => {
  // 先注销上一次注册,与 onMounted/onActivated 双触发幂等,不产生重复注册
  clearRegisteredAbilities();
  // 注册 AI 助手页面能力(同名多实例由注册表栈管理,取栈顶存活项)
  unregisterAbilityFns.push(
    registerCustomPageAbility({
      name: 'view_capability',
      toolName: 'page_view_capability',
      label: '判断页面能否配置视图',
      description:
        '判断当前页面是否支持指定类型视图,返回支持结果、页面标识、启用的视图类型、权限与现有视图数量。',
      promptGuidelines: [
        'view_type 可选;省略时返回全部支持情况',
        'supported=false 时按 reason 如实告知用户,不要继续执行视图操作',
      ],
      parameters: ViewCapabilityParamsSchema,
      execute: handleViewCapability,
    }),
    registerCustomPageAbility({
      name: 'view_config_data',
      toolName: 'page_view_config_data',
      label: '获取视图配置所需数据',
      description:
        '获取当前页面配置指定类型视图所需的基础数据:字段清单、搜索字段、当前视图配置、现有视图列表与样例数据。',
      promptGuidelines: [
        '构造 view_data 前必须先调用',
        '返回的 columns / search_config 是字段名与枚举的唯一依据',
      ],
      parameters: ViewConfigDataParamsSchema,
      execute: handleViewConfigData,
    }),
    registerCustomPageAbility({
      name: 'view_get',
      toolName: 'page_view_get',
      label: '读取已有视图配置',
      description: '读取指定类型下单个已有视图的完整配置,用于复用旧视图或向用户说明已有视图内容。',
      promptGuidelines: ['uid 或 name 至少提供一个(uid 优先)'],
      parameters: ViewLookupParamsSchema,
      execute: handleViewGet,
    }),
    registerCustomPageAbility({
      name: 'view_config',
      toolName: 'page_view_config',
      aliases: ['view_add', 'page_view_add'],
      label: '配置视图',
      description:
        '应用指定类型的视图配置。支持保存为新视图(apply_mode=create)或仅临时应用到当前页面(apply_mode=temporary)。',
      promptGuidelines: [
        'apply_mode=create 时 name 必填,会真实创建视图',
        'apply_mode=temporary 时不要传 name,仅临时作用于当前页面',
        `visibility 可选 personal(默认)/public。${WRITE_PROMPT}`,
      ],
      parameters: ViewConfigParamsSchema,
      timeoutMs: 60000,
      write: true,
      execute: handleViewConfig,
    }),
    registerCustomPageAbility({
      name: 'view_update',
      toolName: 'page_view_update',
      label: '更新视图配置',
      description: '覆盖更新某个已有视图的完整配置数据。宿主会先校验格式,再弹窗请用户确认。',
      promptGuidelines: ['uid 或 name 至少提供一个(uid 优先)', `覆盖不可撤销。${WRITE_PROMPT}`],
      parameters: ViewUpdateParamsSchema,
      timeoutMs: 60000,
      write: true,
      execute: handleViewUpdate,
    }),
    registerCustomPageAbility({
      name: 'view_goto',
      toolName: 'page_view_goto',
      label: '跳转到已有视图',
      description: '切换到指定类型的已有视图,并立即把该视图配置应用到当前页面。',
      promptGuidelines: ['uid 或 name 至少提供一个(uid 优先)'],
      parameters: ViewLookupParamsSchema,
      execute: handleViewGoto,
    }),
    registerCustomPageAbility({
      name: 'view_rename',
      toolName: 'page_view_rename',
      label: '重命名视图',
      description: '重命名指定类型下的某个已有视图。',
      promptGuidelines: [WRITE_PROMPT],
      parameters: ViewRenameParamsSchema,
      write: true,
      execute: handleViewRename,
    }),
    registerCustomPageAbility({
      name: 'view_delete',
      toolName: 'page_view_delete',
      label: '删除视图',
      description: '删除指定类型下的某个已有视图。删除后不可恢复。',
      promptGuidelines: ['删除前必须和用户明确确认'],
      parameters: ViewLookupParamsSchema,
      write: true,
      execute: handleViewDelete,
    }),
    registerCustomPageAbility({
      name: 'view_set_default',
      toolName: 'page_view_set_default',
      label: '设为默认视图',
      description: '把指定类型下的某个已有视图设为默认视图。',
      promptGuidelines: [WRITE_PROMPT],
      parameters: ViewLookupParamsSchema,
      write: true,
      execute: handleViewSetDefault,
    }),
  );
  // 注册 AI 助手快速操作标签按钮(与页面能力同生命周期;相同 label 由注册表压栈去重)
  unregisterAbilityFns.push(
    registerQuickAction({
      label: '条件查询',
      sendDirect: false,
      prompt:
        '请根据下面的查询条件,不创建或保存视图,直接在当前页面进行数据查询展示(view_config,apply_mode=temporary,view_type=normal):\n',
    }),
    registerQuickAction({
      label: '新建标准视图',
      sendDirect: false,
      prompt:
        '请帮我新建一个标准视图。先调用页面能力获取视图配置所需数据(view_type=normal),再按下面的要求创建并应用到当前页面(apply_mode=create):\n',
    }),
    registerQuickAction({
      label: '新建透视表',
      sendDirect: false,
      prompt:
        '请帮我新建一个透视表视图。先调用页面能力获取视图配置所需数据(view_type=pivot),再按下面的行/列/值及汇总方式要求创建并应用(apply_mode=create):\n',
    }),
    registerQuickAction({
      label: '新建透视图',
      sendDirect: false,
      prompt:
        '请帮我新建一个透视图视图。先调用页面能力获取视图配置所需数据(view_type=pivot_charts),再按下面的维度/值/图表类型要求创建并应用(apply_mode=create):\n',
    }),
  );
};
// 普通挂载 + keep-alive 首次挂载/恢复都触发注册;内部先清后注,重复调用安全
onActivated(() => {
  if (props.enable_ai_abilities) {
    registerPageAbilities();
  }
});
onDeactivated(clearRegisteredAbilities);
onUnmounted(clearRegisteredAbilities);

// 切换左侧 tab 时同步更新 URL hash：
// keep-alive 下被隐藏的组件实例被缓存，其 currentView 不再变化，
// 左侧视图组件（viewsConfig）的 hash 更新 watch 不会再触发，
// 因此这里在每次切换模式时显式把「模式前缀 + 当前视图 uid」写入 hash，
// 保证刷新页面后能根据 hash 前缀恢复正确的表格模式。
watch(
  () => props.tableMode,
  async newMode => {
    // 详情表不写 hash：抽屉里的表格与主页面共用同一个 URL，
    // 写 hash 会污染主页面的视图恢复（enable_hash_sync 为 false 时跳过）
    if (!props.enable_hash_sync) {
      return;
    }
    await nextTick();
    const prefix =
      newMode === 'pivot' ? 'pivot:' : newMode === 'pivot_charts' ? 'pivotcharts:' : '';
    const uid = activeTableRef.value?.currentView?.uid || '';
    window.location.hash = `${prefix}${uid}`;
  },
);
const toggleOpen = () => {
  leftOpen.value = !leftOpen.value;
};

// ============ AI 助手视图能力(自动添加 / 跳转 / 复用视图) ============
// 注册进 window 级能力注册表,由 liyu-pc-lib 的 AI 助手提供者按能力名分发。
const VIEW_SYSTEM_EDIT = 'view_system_edit';
const is_view_system_edit_auth = ref(userStore.CHECK_AUTH(VIEW_SYSTEM_EDIT));
const env = useEnv();

const getModeRef = (view_type: ViewType) => {
  if (view_type === 'pivot') return pivotTableRef;
  if (view_type === 'pivot_charts') return pivotChartsRef;
  return tableProRef;
};
const getViewKey = (view_type: ViewType) => {
  const keyPrefix =
    view_type === 'pivot' ? 'pivot-' : view_type === 'pivot_charts' ? 'pivotcharts-' : '';
  return `table_pro_${keyPrefix}${props.view_key_namespace}-${props.modalType}`;
};
const canUseViewType = (view_type: ViewType) => {
  // 三种模式的数据均依赖 queryData;pivot/pivot_charts 由其派生接口 URL 或回退前端聚合
  return !!props.pageConfig.queryData;
};
const getPageTitle = () =>
  (route.query?.customTitle as string) || (route.meta.title as string) || '';

// 获取配置视图所需的基础数据(字段清单/现有视图/样例行/生效条件)
const getPageMeta = async (view_type: ViewType) => {
  const ref = getModeRef(view_type).value;
  const view_list = await fetchViewsForMode(view_type);
  return {
    view_type,
    columns: props.pageConfig.baseColumns || [],
    search_config: getSearchConfigList(),
    current_view_data: ref?.currentView?.data || null,
    view_list: view_list.map((v: any) => ({
      id: v.id,
      uid: v.uid,
      name: v.name,
      is_default: v.is_default,
      user_id: v.user_id,
      is_system: v.is_system,
    })),
    sample_rows: ref ? (ref.dataSource || []).slice(0, 5) : [],
    base_conditions: props.pageConfig.advanced_base_conditions || [],
    query_params: props.pageConfig.query_params || {},
  };
};

// 等待模式切换后目标组件实例就绪(keep-alive 缓存实例,切换后即可用)
const waitForModeRef = async (view_type: ViewType) => {
  for (let i = 0; i < 30; i++) {
    await nextTick();
    const ref = getModeRef(view_type).value;
    if (ref) return ref;
  }
  return null;
};

// 应用已存在/新创建的视图到目标模式(切模式 + 设置当前视图 + 更新 hash),view_config(create) 与 view_goto 共用
const activateView = async (view: any, view_type: ViewType) => {
  if (!view || !view.uid) return;
  let target = getModeRef(view_type).value;
  if (view_type !== props.tableMode) {
    emit('changeMode', view_type);
    target = await waitForModeRef(view_type);
  }
  if (target) {
    target.currentView = view;
    await target.setViewData(view.data);
  }
  const prefix =
    view_type === 'pivot' ? 'pivot:' : view_type === 'pivot_charts' ? 'pivotcharts:' : '';
  window.location.hash = `${prefix}${view.uid}`;
};

const TEMP_VIEW_ID = 'no-view';

const activateTemporaryView = async (targetViewType: ViewType, view_data: any) => {
  const normalized = normalizeViewData(view_data, targetViewType);
  let target = getModeRef(targetViewType).value;
  if (targetViewType !== props.tableMode) {
    emit('changeMode', targetViewType);
    target = await waitForModeRef(targetViewType);
  }
  if (!target) return viewError('目标表格实例未就绪,请稍后重试');
  target.currentView = {
    id: TEMP_VIEW_ID,
    uid: '',
    name: '临时配置',
    is_temporary: true,
    data: normalized,
  };
  await target.setViewData(normalized);
  const prefix =
    targetViewType === 'pivot' ? 'pivot:' : targetViewType === 'pivot_charts' ? 'pivotcharts:' : '';
  window.location.hash = `${prefix}`;
  return {
    ok: true,
    data: {
      mode: 'temporary',
      applied: true,
      view_type: targetViewType,
    },
  };
};

const viewError = (message: string) => ({ ok: false as const, error: message });
// 获取指定模式的视图列表。优先用已挂载组件实例;未挂载(keep-alive 只渲染当前模式,
// 如标准 tab 下询问透视表/透视图视图)时,按 view_key 直接调接口拉取,不依赖组件实例。
const fetchViewsForMode = async (view_type: ViewType) => {
  const ref = getModeRef(view_type).value;
  if (ref) {
    if ((!ref.viewDataList || ref.viewDataList.length === 0) && ref.fetchViews) {
      try {
        await ref.fetchViews();
      } catch {
        // 视图列表加载失败时按空列表处理
      }
    }
    return ref.viewDataList || [];
  }
  try {
    return (
      (await fetchViewsData(
        getViewKey(view_type),
        () => ({ current: 1, pageSize: 500, is_pagination: true }),
        view_type === 'normal' ? props.pageConfig.countData : undefined,
        getConfig(view_type),
      )) || []
    );
  } catch (e) {
    console.error('按 view_key 拉取视图列表失败', e);
    return [];
  }
};

// 定位目标视图(uid 优先 / 名称匹配),返回 { ref, view, list } 或 { error }
const resolveTargetView = async (
  view_type: ViewType,
  uid?: string | null,
  name?: string | null,
) => {
  const list = await fetchViewsForMode(view_type);
  const view = findViewByUidOrName(list, uid, name);
  if (!view) {
    const names = list.map((v: any) => v.name).join('、');
    return { error: `未找到视图「${name || uid}」${names ? `,现有视图:${names}` : ''}`, list };
  }
  return { ref: getModeRef(view_type).value, view, list };
};

// 系统视图的修改/删除/设默认需要 view_system_edit 权限,返回空串表示允许
const checkViewOperateAuth = (view: any) => {
  if (view.is_system && !is_view_system_edit_auth.value) {
    return '系统视图需要 view_system_edit 权限才能操作,请先授权或改用个人/公共视图';
  }
  return '';
};

// view_capability: 判断当前页面能否配置指定类型视图
const handleViewCapability = async (params: ViewCapabilityParams) => {
  const requested = params?.view_type;
  if (requested !== undefined && !isViewType(requested)) {
    return viewError(`未知视图类型: ${requested}(可选: normal / pivot / pivot_charts)`);
  }
  const enabled = props.pageConfig.enable_view_types || ['normal', 'pivot', 'pivot_charts'];
  const types: ViewType[] = requested
    ? [requested as ViewType]
    : (enabled as string[] as ViewType[]);
  const results = types.map(view_type => {
    let supported = !!props.pageConfig.isView && enabled.includes(view_type);
    let reason = '';
    if (!props.pageConfig.isView) {
      supported = false;
      reason = '该页面未启用视图(isView=false)';
    } else if (!enabled.includes(view_type)) {
      supported = false;
      reason = `该页面未启用「${view_type}」类型视图`;
    } else if (!canUseViewType(view_type)) {
      supported = false;
      reason = '该模式数据接口不可用';
    }
    return { view_type, supported, reason };
  });
  const firstType = requested ? (requested as ViewType) : 'normal';
  return {
    ok: true,
    data: {
      is_view_page: !!props.pageConfig.isView,
      supported: requested ? results[0].supported : results.some(r => r.supported),
      results,
      page: {
        app_flag: props.app_flag,
        modalType: props.modalType,
        title: getPageTitle(),
        tableMode: props.tableMode,
        enable_view_types: enabled,
        view_key: getViewKey(firstType),
      },
      auth: {
        can_create_personal: true,
        can_create_public: true,
        can_create_system: is_view_system_edit_auth.value,
      },
      view_count: (await fetchViewsForMode(firstType)).length,
    },
  };
};

// view_config_data: 获取配置视图所需基础数据
const handleViewConfigData = async (params: ViewConfigDataParams) => {
  const view_type = params?.view_type;
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  if (!canUseViewType(view_type)) return viewError('该模式数据接口不可用');
  return { ok: true, data: await getPageMeta(view_type) };
};

// view_get: 读取单个已有视图完整配置(复用旧视图 / 告知用户)
const handleViewGet = async (params: ViewLookupParams) => {
  const view_type = params?.view_type;
  const { uid, name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  const view = target.view;
  return {
    ok: true,
    data: {
      view: {
        id: view.id,
        uid: view.uid,
        name: view.name,
        is_default: view.is_default,
        user_id: view.user_id,
        is_system: view.is_system,
        data: view.data,
      },
    },
  };
};

// view_config: 复用同一份视图配置结构,支持保存为新视图或仅临时应用到当前页面
const handleViewConfig = async (params: ViewConfigParams) => {
  const { view_type, name, view_data, is_system, activate, apply_mode = 'create' } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const v = validateViewConfigParams(
    { view_type, name, view_data, is_system, apply_mode },
    {
      columns: props.pageConfig.baseColumns || [],
      search_config: getSearchConfigList(),
    },
  );
  if (!v.valid) return viewError(v.error!);
  if (apply_mode === 'temporary') {
    return activateTemporaryView(view_type, view_data);
  }
  if (is_system && !is_view_system_edit_auth.value) {
    return viewError('没有「设为系统视图」权限(view_system_edit),请改用个人/公共视图');
  }
  const visibility = params?.visibility === 'public' ? 'public' : 'personal';
  const normalized = normalizeViewData(view_data, view_type);
  let res: any;
  try {
    res = await create_view_settings({
      org_id: userStore.currentOrg?.id,
      user_id: is_system || visibility === 'public' ? null : userStore.userid,
      name: String(name).trim(),
      view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
      view_menu__flag: getViewKey(view_type),
      view_menu__name: getPageTitle(),
      is_system: !!is_system,
      data: normalized,
    });
  } catch (e: any) {
    return viewError(`创建视图失败: ${e?.message || e || '未知错误'}`);
  }
  const ref = getModeRef(view_type).value;
  if (ref?.fetchViews) {
    try {
      await ref.fetchViews();
    } catch {
      // 刷新失败不阻断激活
    }
  }
  if (activate !== false) {
    const created = findViewByUidOrName(ref?.viewDataList || [], res?.uid, name) || {
      ...(res || {}),
      uid: res?.uid || '',
      name: String(name).trim(),
      data: normalized,
    };
    await activateView(created, view_type);
  }
  return {
    ok: true,
    data: {
      mode: 'create',
      id: res?.id,
      uid: res?.uid,
      name: String(name).trim(),
    },
  };
};

// view_goto: 跳转到已有视图(按钮动作)
const handleViewGoto = async (params: ViewLookupParams) => {
  const { view_type, uid, name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  await activateView(target.view, view_type);
  return {
    ok: true,
    data: { view: { id: target.view.id, uid: target.view.uid, name: target.view.name } },
  };
};

// view_rename: 重命名已有视图
const handleViewRename = async (params: ViewRenameParams) => {
  const view_type = params?.view_type;
  const { uid, name, new_name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  if (typeof new_name !== 'string' || !new_name.trim()) return viewError('new_name 不能为空');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  const authErr = checkViewOperateAuth(target.view);
  if (authErr) return viewError(authErr);
  try {
    await update_view_settings({
      org_id: userStore.currentOrg?.id,
      view_settings_id: target.view.id,
      name: String(new_name).trim(),
    });
  } catch (e: any) {
    return viewError(`重命名失败: ${e?.message || e || '未知错误'}`);
  }
  if (target.ref?.fetchViews) {
    try {
      await target.ref.fetchViews();
    } catch {
      // 刷新失败不影响返回
    }
  }
  // 当前激活的视图被重命名时同步更新引用,保持视图栏/表格标题一致
  const cur = target.ref?.currentView;
  if (cur && cur.id === target.view.id) {
    cur.name = String(new_name).trim();
  }
  return {
    ok: true,
    data: { id: target.view.id, uid: target.view.uid, name: String(new_name).trim() },
  };
};

// view_delete: 删除已有视图(不可恢复,由大模型与用户确认后调用)
const handleViewDelete = async (params: ViewLookupParams) => {
  const view_type = params?.view_type;
  const { uid, name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  const authErr = checkViewOperateAuth(target.view);
  if (authErr) return viewError(authErr);
  try {
    await delete_view_settings({
      org_id: userStore.currentOrg?.id,
      view_settings_id: target.view.id,
    });
  } catch (e: any) {
    return viewError(`删除失败: ${e?.message || e || '未知错误'}`);
  }
  if (target.ref?.fetchViews) {
    try {
      await target.ref.fetchViews();
    } catch {
      // 刷新失败不影响返回
    }
  }
  // 删除的是当前激活视图 → 回到默认视图
  const cur = target.ref?.currentView;
  if (cur && cur.id === target.view.id) {
    target.ref.currentView = null;
    await target.ref.setViewData(null);
  }
  return { ok: true, data: { id: target.view.id, uid: target.view.uid, name: target.view.name } };
};

// view_set_default: 设为默认视图(打开页面时自动应用)
const handleViewSetDefault = async (params: ViewLookupParams) => {
  const view_type = params?.view_type;
  const { uid, name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  const authErr = checkViewOperateAuth(target.view);
  if (authErr) return viewError(authErr);
  try {
    await set_view_settings_default({
      org_id: userStore.currentOrg?.id,
      view_settings_id: target.view.id,
    });
    await refresh_view_settings_cache({
      org_id: userStore.currentOrg?.id,
      view_settings_id: target.view.id,
    });
  } catch (e: any) {
    return viewError(`设置默认失败: ${e?.message || e || '未知错误'}`);
  }
  if (target.ref?.fetchViews) {
    try {
      await target.ref.fetchViews();
    } catch {
      // 刷新失败不影响返回
    }
  }
  return { ok: true, data: { id: target.view.id, uid: target.view.uid, name: target.view.name } };
};

// 覆盖视图前的用户确认弹窗(与 hooks/view.ts 的 saveView 弹窗一致:覆盖不可撤销)
const confirmOverwriteView = (view: any): Promise<boolean> =>
  new Promise(resolve => {
    Modal.confirm({
      title: '确定覆盖视图吗？',
      content: `视图「${view.name}」原有的数据会被新配置覆盖`,
      icon: h(ExclamationCircleOutlined),
      okText: '确定',
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

// view_update: 覆盖更新已有视图的配置数据(宿主校验格式 → 弹窗确认 → 更新 → 应用)
const handleViewUpdate = async (params: ViewUpdateParams) => {
  const { view_type, view_data, is_system } = params || {};
  const { uid, name } = params || {};
  if (!isViewType(view_type)) return viewError('view_type 必填: normal | pivot | pivot_charts');
  const target = await resolveTargetView(view_type, uid, name);
  if (target.error) return viewError(target.error);
  const v = validateViewData(view_type, view_data, {
    columns: props.pageConfig.baseColumns || [],
    search_config: getSearchConfigList(),
  });
  if (!v.valid) return viewError(v.error!);
  const authErr = checkViewOperateAuth(target.view);
  if (authErr) return viewError(authErr);
  if (is_system !== undefined && typeof is_system !== 'boolean') {
    return viewError('is_system 必须是布尔值');
  }
  // 弹窗确认:覆盖不可撤销(用户取消则返回失败)
  const confirmed = await confirmOverwriteView(target.view);
  if (!confirmed) return { ok: false, error: '用户取消了更新视图' };
  const normalized = normalizeViewData(view_data, view_type);
  try {
    await update_view_settings({
      org_id: userStore.currentOrg?.id,
      view_settings_id: target.view.id,
      ...(is_system !== undefined ? { is_system: !!is_system } : {}),
      data: normalized,
    });
  } catch (e: any) {
    return viewError(`更新视图失败: ${e?.message || e || '未知错误'}`);
  }
  if (target.ref?.fetchViews) {
    try {
      await target.ref.fetchViews();
    } catch {
      // 刷新失败不影响返回
    }
  }
  // 若目标视图是当前激活视图,立即应用新配置
  const cur = target.ref?.currentView;
  if (cur && cur.id === target.view.id && target.ref) {
    target.ref.currentView = { ...cur, data: normalized };
    await target.ref.setViewData(normalized);
  }
  return { ok: true, data: { id: target.view.id, uid: target.view.uid, name: target.view.name } };
};

const simpleItemToSearchItem = (
  item: any,
  model: Record<string, any>,
  options: Record<string, any>,
): SearchConfig => {
  const res: SearchConfig = {
    ...item,
    name: undefined,
    field_name: item.name,
    operator_list: [],
  };
  if (options[item.name]) {
    res.options = options[item.name];
  }
  if (model[item.name]) {
    res.default_value = model[item.name];
  }
  if (['rangepicker', 'monthrangepicker'].includes(res.type)) {
    res.default_operator = 'between';
  } else {
    res.default_operator = 'eq';
  }
  return res;
};

// 构建搜索字段列表(普通模式由 searchConfig.fields 转换,高级模式展平分组)。
// getConfig 与 AI 助手 getPageMeta 共用,保证「模型看到的字段」与页面搜索一致。
const getSearchConfigList = (): SearchConfig[] => {
  const enable_search =
    props.pageConfig.is_show_normal_search || props.pageConfig.is_show_advantage;
  const is_advanced_search = props.pageConfig.is_show_advantage;
  const search_config_list: SearchConfig[] = [];
  if (enable_search) {
    if (is_advanced_search) {
      for (const group of props.pageConfig.base_search_condition_config_list || []) {
        for (const item of group.group || []) {
          search_config_list.push({ ...item, group_name: group.name });
        }
      }
    } else {
      const searchConfig = props.pageConfig.searchConfig;
      if (searchConfig) {
        for (const item of searchConfig.fields) {
          search_config_list.push(
            simpleItemToSearchItem(item, searchConfig.model || {}, searchConfig.options || {}),
          );
        }
      }
    }
  }
  return search_config_list;
};
const getRouteParams = () => {
  let query_json = {};
  if (route.query?.query_json) {
    query_json = JSON.parse(route.query?.query_json as any);
  }
  const routeQueryParams = JSON.parse(JSON.stringify({ ...route.query, ...query_json }));
  const del_keys = [
    't',
    'query_json',
    'is_router',
    'is_global',
    'is_fuzzy_search',
    'customerTitle',
  ];
  for (const key of del_keys) {
    delete routeQueryParams[key];
  }
  if (routeQueryParams?.ext_search_keys) {
    let ext_search_keys = routeQueryParams.ext_search_keys;
    ext_search_keys = ext_search_keys.split(',').map(item => item.trim());
    const _ext_search = {};
    ext_search_keys.forEach((key: string) => {
      _ext_search[key] = routeQueryParams[key];
    });
    return _ext_search;
  } else {
    return routeQueryParams;
  }
};
const getConfig = (mode: 'normal' | 'pivot' | 'pivot_charts'): any => {
  const isNormal = mode === 'normal';
  const isPivot = mode === 'pivot';
  const isPivotCharts = mode === 'pivot_charts';
  let keyPrefix = '';
  let hashPrefix = '';
  if (isPivot) {
    keyPrefix = 'pivot-';
    hashPrefix = 'pivot:';
  } else if (isPivotCharts) {
    keyPrefix = 'pivotcharts-';
    hashPrefix = 'pivotcharts:';
  }
  const enable_search =
    props.pageConfig.is_show_normal_search || props.pageConfig.is_show_advantage;
  const is_advanced_search = props.pageConfig.is_show_advantage;
  // 多组查询生效值：注入点传布尔时强制（详情表默认传 false = 单组高级查询），未传时跟随目标页面配置。
  // 恒以 is_show_advantage 为前提（多组查询只是高级查询的一种形态，页面没开高级查询时强制 true 无意义），
  // 避免 simple 搜索形态 + multigroups=true 的矛盾组合
  const enable_multigroups =
    (props.enable_advanced_search_multigroups === undefined
      ? is_advanced_search
      : props.enable_advanced_search_multigroups) && is_advanced_search;
  const search_config_list = getSearchConfigList();
  const default_advanced_search_multigroups: SearchGroupItem[] = [];

  if (enable_search && enable_multigroups) {
    for (const group of props.pageConfig.default_search_condition_config_list || []) {
      const defaultItem: SearchGroupItem = {
        id: genID(),
        conditions: [],
        viewSearchConfig: {},
      };
      for (const item of search_config_list) {
        defaultItem.viewSearchConfig[item.field_name] = {
          checked: false,
          order: 9999,
        };
      }
      let order = 0;
      for (const item of group || []) {
        defaultItem.conditions.push(item);
        defaultItem.viewSearchConfig[item.field_name] = {
          checked: true,
          order: order,
        };
        order++;
      }
      default_advanced_search_multigroups.push(defaultItem);
    }
  }
  let hash = '';
  if (window.location.hash) {
    hash = window.location.hash.substring(1); // 去掉 # 符号
    // Filter hash by mode: pivot hash starts with 'pivot:', pivotcharts hash starts with 'pivotcharts:'
    if (hashPrefix) {
      if (hash.startsWith(hashPrefix)) {
        hash = hash.substring(hashPrefix.length);
      } else {
        hash = '';
      }
    } else {
      // normal mode: ignore pivot: and pivotcharts: hashes
      if (hash.startsWith('pivot:') || hash.startsWith('pivotcharts:')) {
        hash = '';
      }
    }
  }
  // 详情表不读主页面 hash 的视图 uid（否则会把主页面的视图套到详情表上）
  if (!props.enable_hash_sync) {
    hash = '';
  }
  let base_conditions = null;
  let query_params: any = {
    ...(props.pageConfig.query_params || {}),
    ...(props.query_params_inject || {}),
  };
  // 详情表的预过滤条件只能走 conditions 通道：后端 check_request_parmes 只认已声明参数，
  // 顶层字段（如 config_id=123）会被静默丢弃。因此有注入条件时强制产出 advanced 形态的
  // conditions；但 enable_advanced_search_multigroups 仍按目标页面原配置，搜索表单 UI 不变。
  const force_advanced_query = !!props.base_conditions_inject?.length;
  if (is_advanced_search || force_advanced_query) {
    let config_base_conditions = null;
    const merged_config_conditions = [
      ...(Array.isArray(props.pageConfig.advanced_base_conditions)
        ? props.pageConfig.advanced_base_conditions
        : []),
      ...(props.base_conditions_inject || []),
    ];
    if (merged_config_conditions.length > 0) {
      config_base_conditions = merged_config_conditions;
    }
    let route_base_conditions = [];
    // 详情表默认不继承主页面路由参数：主页面 URL 上的业务过滤字段在子表里通常不存在，
    // 带上会导致后端报「xxx字段不存在」而整条查询失败
    if (props.inherit_route_params) {
      Object.entries(getRouteParams()).forEach(([key, value]) => {
        if (!Array.isArray(value)) {
          value = [value];
        }
        route_base_conditions.push({
          field_name: key,
          operator: 'in',
          value: value,
          format: 'input',
          class_name: 'str',
        });
      });
    }
    if (config_base_conditions?.length > 0 && route_base_conditions?.length > 0) {
      base_conditions = [
        { op: 'and', children: config_base_conditions },
        { op: 'and', children: route_base_conditions },
      ];
    } else if (config_base_conditions?.length > 0) {
      base_conditions = config_base_conditions;
    } else if (route_base_conditions?.length > 0) {
      base_conditions = route_base_conditions;
    }
  } else {
    Object.assign(query_params, getRouteParams());
  }
  if (filter_id_key.value) {
    query_params.filter_id_key = filter_id_key.value;
  }
  const columns = (props.pageConfig.baseColumns || []).filter(item => item.dataIndex !== 'action');
  if (userStore.HAS_ACTION_BTN(props.pageConfig)) {
    columns.push({
      title: t('操作'),
      width: 50,
      dataIndex: 'action',
      checked: true,
      fixed: 'right',
      cellComponent: 'action',
    });
  }
  return {
    key: `${keyPrefix}${props.view_key_namespace}-${props.modalType}`,
    title:
      props.title_inject ||
      (route.query?.customTitle as string) ||
      (route.meta.title as string) ||
      '',
    tableSize: 'small',
    search_display_type: props.search_display_type,
    search_type: is_advanced_search || force_advanced_query ? 'advanced' : 'simple',
    is_selection: props.pageConfig.is_selection,
    is_pagination: props.pageConfig.is_pagination,
    hide_header: props.pageConfig.hide_header,
    list_url: props.pageConfig.queryData,
    setting_mode: 'drawer',
    setting_drawer_width: props.setting_drawer_width,
    pivot_charts_url: props.pageConfig.queryData
      ? props.pageConfig.queryData.replace('_list', '_pivot_charts_rust')
      : undefined,
    pivot_table_url: props.pageConfig.queryData
      ? props.pageConfig.queryData.replace('_list', '_pivot_table_rust')
      : undefined,
    pivot_table_max_records: props.pageConfig.pivot_table_max_records,
    // search_column_count: is_advanced_search ? 2 : null,
    search_config: search_config_list,
    columns: columns,
    show_current_view: true,
    enable_custom_filter: true,
    query_field_search_config_url: props.pageConfig.queryData
      ? props.pageConfig.queryData
          .replace('/query_', '/obtain_')
          .replace('_list', '_field_search_config')
      : undefined,
    enable_advanced_search_multigroups: enable_multigroups,
    default_advanced_search_multigroups: default_advanced_search_multigroups,
    condition_group_config_list: enable_multigroups
      ? props.pageConfig.condition_group_config_list || []
      : null,
    query_group_url: props.pageConfig.queryConditionGroupData,
    refreshType: props.pageConfig.refresh_type as RefreshType,
    pageSize: props.pageConfig.pageSize,
    pageSizeOptions: props.pageConfig.page_size_options,
    view_uid: hash,
    base_conditions: base_conditions,
    query_params: query_params,
    btn_list: props.pageConfig.btn_list || [],
    head_btn_list: props.pageConfig.head_btn_list || [],
    batch_update_list: props.pageConfig.batch_update_list || [],
    required_condition: props.pageConfig.required_condition,
    count_url: isNormal ? props.pageConfig.countData : undefined,
    customRow: props.pageConfig.customRow || undefined,
    customHeaderCell: props.pageConfig.customHeaderCell || undefined,
    customCell: props.pageConfig.customCell || undefined,
    rowSelectionType: (props.pageConfig.rowSelection?.type || 'checkbox') as RowSelectionType,
    rowSelectionCheckStrictly: props.pageConfig.rowSelection?.checkStrictly || false,
    hide_index_column: true,
    get_db_alias: props.pageConfig.get_db_alias,
    disabledShowSearchGroupName: true,
    is_click_heightlight: !!props.pageConfig.row_click_detail_event_key,
    cellComponentExt: {
      titleList: props.pageConfig.titleList,
      modalType: props.modalType,
      app_flag: props.app_flag,
      getData: props.pageConfig.getData,
      api_fields: props.pageConfig.api_fields,
      pageConfig: props.pageConfig,
    },
  };
};
const handleFilterForIdKeyStart = () => {
  // 透视表双击定位：开始向后端请求（接口可能耗时较久），先展示全局 loading 遮罩
  filterLoading.value = true;
};
const handleFilterForIdKey = (params: {
  filter_id_key: string;
  filterList: { label: string; value: string }[];
  matchValueLabel: string;
}) => {
  // 后端接口已完成（成功或失败），先关闭全局 loading 遮罩，再进行后续 tab 页跳转
  filterLoading.value = false;
  if (params) {
    filter_id_key.value = params.filter_id_key;
    filterList.value = params.filterList;
    matchValueLabel.value = params.matchValueLabel;
    normalConfig.value = getConfig('normal');
    emit('changeMode', 'normal');
    nextTick(() => {
      tableProRef.value.currentView = { id: 'no-view' };
      tableProRef.value.setViewData(null);
    });
  } else {
    // 定位失败（pivotTable 在接口异常时回传 null），给出明确反馈
    message.error('数据定位失败，请重试');
  }
};
const onClearFilterIdkey = () => {
  filter_id_key.value = '';
  normalConfig.value = getConfig('normal');
};

// defineExpose 在 setup 末尾调用:它同步读取引用的变量,必须在所有 const 定义之后,
// 否则会触发 TDZ(Cannot access 'X' before initialization)。
defineExpose({
  tableProRef,
  pivotTableRef,
  pivotChartsRef,
  activeTableRef,
  getPageMeta,
});
</script>

<style scoped lang="less">
.table-wrapper {
  flex: 1;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toggle-button {
  cursor: pointer;
  height: 46px;
  line-height: 33px;
  font-size: 20px;
  &:hover {
    color: #1890ff;
    filter: drop-shadow(2px 2px 2px #bddfff);
  }
}
.filter-message {
  display: flex;
  gap: 14px;
}
</style>
